/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminUsers\AdminUsers.js
 * 
 * ВЕРСИЯ: 2.0.0 - С ИСПОЛЬЗОВАНИЕМ API.JS
 * ДАТА: 15 ноября 2025
 * СТАТУС: ✅ ГОТОВО
 * 
 * ИСПОЛЬЗУЕТ: adminAPI из src/services/api.js
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';
import './AdminUsers.css';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(false);

  // Загрузка пользователей при монтировании компонента
  useEffect(() => {
    fetchUsers();
  }, []);

  // Фильтрация при изменении фильтров
  useEffect(() => {
    applyFilters();
  }, [users, searchQuery, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📝 Загружаю пользователей через adminAPI...');
      const response = await adminAPI.users.getAllUsers();

      if (response.success && response.data) {
        setUsers(response.data);
        console.log(`✅ Загружено пользователей: ${response.data.length}`);
      } else {
        throw new Error(response.error || 'Ошибка загрузки данных');
      }
    } catch (err) {
      console.error('❌ Ошибка при загрузке пользователей:', err);
      setError(err.message);
      // Используем тестовые данные если ошибка
      setUsers(getMockUsers());
    } finally {
      setLoading(false);
    }
  };

  // Тестовые данные для показа интерфейса
  const getMockUsers = () => [
    {
      id: 1,
      email: 'alice@test.com',
      username: 'Alice',
      phone: '+1234567890',
      role: 'user',
      status: 'active',
      created_at: '2025-11-01T10:00:00Z',
      tier: 0
    },
    {
      id: 2,
      email: 'business@test.com',
      username: 'Business',
      phone: '+1234567891',
      role: 'business',
      status: 'active',
      created_at: '2025-11-02T10:00:00Z',
      tier: 1
    },
    {
      id: 3,
      email: 'admin@test.com',
      username: 'Admin',
      phone: '+1234567892',
      role: 'admin',
      status: 'active',
      created_at: '2025-10-01T10:00:00Z',
      tier: 0
    },
    {
      id: 4,
      email: 'blocked-user@test.com',
      username: 'BlockedUser',
      phone: '+1234567893',
      role: 'user',
      status: 'blocked',
      created_at: '2025-10-01T10:00:00Z',
      tier: 0
    }
  ];

  const applyFilters = () => {
    let filtered = users;

    // Фильтр по ролям
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Фильтр по статусу
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    // Поиск по email или имени
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(query) ||
        (user.username && user.username.toLowerCase().includes(query))
      );
    }

    setFilteredUsers(filtered);
  };

  const handleBlockUser = async (userId) => {
    if (!window.confirm('Вы уверены что хотите заблокировать этого пользователя?')) {
      return;
    }

    try {
      setActionLoading(true);
      console.log(`🚫 Блокирую пользователя ${userId}...`);
      
      const response = await adminAPI.users.blockUser(userId);

      if (response.success) {
        console.log(`✅ Пользователь ${userId} заблокирован`);
        // Обновляем локальный список
        setUsers(users.map(user =>
          user.id === userId ? { ...user, status: 'blocked' } : user
        ));
        alert('✅ Пользователь заблокирован');
      } else {
        throw new Error(response.error || 'Ошибка блокировки');
      }
    } catch (err) {
      console.error('❌ Ошибка:', err);
      alert(`❌ Ошибка при блокировке: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblockUser = async (userId) => {
    if (!window.confirm('Вы уверены что хотите разблокировать этого пользователя?')) {
      return;
    }

    try {
      setActionLoading(true);
      console.log(`🔓 Разблокирую пользователя ${userId}...`);
      
      const response = await adminAPI.users.unblockUser(userId);

      if (response.success) {
        console.log(`✅ Пользователь ${userId} разблокирован`);
        // Обновляем локальный список
        setUsers(users.map(user =>
          user.id === userId ? { ...user, status: 'active' } : user
        ));
        alert('✅ Пользователь разблокирован');
      } else {
        throw new Error(response.error || 'Ошибка разблокировки');
      }
    } catch (err) {
      console.error('❌ Ошибка:', err);
      alert(`❌ Ошибка при разблокировке: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('⚠️ ВНИМАНИЕ! Вы уверены что хотите удалить этого пользователя? Это необратимо!')) {
      return;
    }

    try {
      setActionLoading(true);
      console.log(`🗑️ Удаляю пользователя ${userId}...`);
      
      const response = await adminAPI.users.deleteUser(userId);

      if (response.success) {
        console.log(`✅ Пользователь ${userId} удален`);
        setUsers(users.filter(user => user.id !== userId));
        alert('✅ Пользователь удален');
      } else {
        throw new Error(response.error || 'Ошибка удаления');
      }
    } catch (err) {
      console.error('❌ Ошибка:', err);
      alert(`❌ Ошибка при удалении: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'user':
        return 'role-user';
      case 'business':
        return 'role-business';
      case 'admin':
        return 'role-admin';
      default:
        return 'role-user';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'blocked':
        return 'status-blocked';
      case 'pending':
        return 'status-pending';
      default:
        return 'status-active';
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      user: '👤 Пользователь',
      business: '🏢 Бизнес',
      admin: '👨‍💼 Администратор'
    };
    return labels[role] || role;
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: '✅ Активен',
      blocked: '🚫 Заблокирован',
      pending: '⏳ В ожидании'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="admin-users-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>⏳ Загрузка пользователей...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-users-page">
      {/* ЗАГОЛОВОК */}
      <div className="admin-users-header">
        <h1>👥 Управление пользователями</h1>
        <p>Всего пользователей: {users.length} | Показано: {filteredUsers.length}</p>
      </div>

      {/* ФИЛЬТРЫ */}
      <div className="admin-filters">
        {/* Поиск */}
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Поиск по email или имени..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {/* Фильтр по роли */}
        <select
          className="filter-select"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">👤 Все роли</option>
          <option value="user">👤 Пользователь</option>
          <option value="business">🏢 Бизнес</option>
          <option value="admin">👨‍💼 Администратор</option>
        </select>

        {/* Фильтр по статусу */}
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">📊 Все статусы</option>
          <option value="active">✅ Активные</option>
          <option value="blocked">🚫 Заблокированные</option>
          <option value="pending">⏳ В ожидании</option>
        </select>

        {/* Кнопка обновления */}
        <button
          className="refresh-btn"
          onClick={fetchUsers}
          disabled={loading || actionLoading}
          title="Обновить список"
        >
          🔄 Обновить
        </button>
      </div>

      {/* ОШИБКА */}
      {error && (
        <div className="error-message">
          ⚠️ {error} (используются тестовые данные)
        </div>
      )}

      {/* ТАБЛИЦА */}
      <div className="admin-users-table">
        {filteredUsers.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Имя</th>
                <th>Роль</th>
                <th>Статус</th>
                <th>Уровень</th>
                <th>Баланс</th>
                <th>Промокоды</th>
                <th>Дата создания</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.email}</td>
                  <td>{user.username || '-'}</td>
                  <td>
                    <span className={`role-badge ${getRoleBadgeColor(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeColor(user.status)}`}>
                      {getStatusLabel(user.status)}
                    </span>
                  </td>
                  <td>Tier {user.tier || 0}</td>
                  <td className="balance-cell">{parseFloat(user.balance || 0).toFixed(2)} DAR</td>
                  <td>{user.promocodes_count || 0} 🎟️</td>
                  <td>{formatDate(user.created_at)}</td>
                  <td className="actions">
                    <button
                      className="action-btn view"
                      title="Просмотр"
                      onClick={() => alert(`ID: ${user.id}\nEmail: ${user.email}\nТелефон: ${user.phone || '-'}`)}
                      disabled={actionLoading}
                    >
                      👁️
                    </button>
                    {user.status !== 'blocked' ? (
                      <button
                        className="action-btn block"
                        title="Заблокировать"
                        onClick={() => handleBlockUser(user.id)}
                        disabled={actionLoading}
                      >
                        🚫
                      </button>
                    ) : (
                      <button
                        className="action-btn unblock"
                        title="Разблокировать"
                        onClick={() => handleUnblockUser(user.id)}
                        disabled={actionLoading}
                      >
                        🔓
                      </button>
                    )}
                    <button
                      className="action-btn delete"
                      title="Удалить"
                      onClick={() => handleDeleteUser(user.id)}
                      disabled={actionLoading}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-users">
            <p>❌ Пользователей не найдено</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
